// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Skeleton,
    Box,
    Container,
    Divider,
    Typography, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel
} from "@mui/material"
import dayjs from "dayjs"

import { Helmet } from "react-helmet";
import MainLayout from "../../components/MainLayout"
import Breadcrumbs from "../../components/Breadcrumbs"
import { TagButton, TagHashButton } from "../../components/TagButton"
import ReviewRatings from "../../components/Tours/ReviewRatings"
import GridGallery from "../../components/GridGallery"
import DetailsSection from "../../components/Tours/DetailsSection"
import ItineraryItem from "../../components/Tours/ItineraryItem"
import CalendarTour from "../../components/Filters/Calendar"
import Schedule from "../../components/Filters/Schedule"
import RadioButtonsFilter from "../../components/Filters/RadioButtonsFilter"
import AddItemsButtonsFilter from "../../components/Filters/AddItemsButtonsFilter"
import PaymentSummary from "../../components/Tours/PaymentSummary"
import CouponBox from "../../components/Tours/CouponBox"
import AdditionalInformationSection from "../../components/Tours/AdditionalInformationSection"
import FAQs from "../../components/Tours/FAQs"
import MediaCard from "../../components/ProductCard3"
import Reviews from "../../components/Tours/Reviews"
import { InfoOutlined } from "@mui/icons-material"

import { useScreenDetector } from "../../hooks/useScreenDetector"
import useFetchAttractions from "../../hooks/API/Tours/useFetchAttractions";
import { getHrefFromGuide, getHrefFromAttraction } from '../../utils/guias'

import { ReactComponent as MedalIcon } from "../../assets/icons/medal.svg"
import { ReactComponent as ClockIcon } from "../../assets/icons/clock.svg"
import { ReactComponent as GlobeIcon } from "../../assets/icons/globe.svg"
import { ReactComponent as PlusIcon } from "../../assets/icons/plus.svg"
import { ReactComponent as CheckIcon } from "../../assets/icons/check.svg"
import { ReactComponent as TrashIcon } from "../../assets/icons/trash_white.svg"

import { tourData, topActivities, reviews } from "./tourData"
import { dataPage } from "../../utils/destinationPageMockData"
import useFetchTourDetails from "../../hooks/API/Tours/useFetchTourDetails"
import RichText from "../../components/RichText"
import { Link, useParams } from "react-router-dom"
import ErrorPage from "../Error"
import useQuote from "../../hooks/API/Tours/useQuote"
import axios from "axios"
import useFetchSuggestTours from "../../hooks/API/Tours/useFetchSuggestTours"
import { useDispatch, useSelector } from "react-redux"
import { addTour, removeExtraActivities, setExtraActivities, selectCartItems } from "../../app/features/cartSlice"
import { isTicketAdded, ticketsEqual, attractionsEqual } from '../../utils/activity'
import { prePrareTourBookingData } from "../../utils/checkout"
import UpdateTourModal from "../../components/UpdateTourModal"
import { min } from "date-fns";



const Tour = () => {
    const { t } = useTranslation(['tours', 'paths', 'components']);
    const { i18n } = useTranslation();
    const params = useParams();

    const { tourDetails = {}, isLoading, error, setIsLoading } = useFetchTourDetails(params.slug);
    const [tour, setTour] = useState({ paxRates: [] });
    const [tourBooking, setTourBooking] = useState({});
    const [expandedTabs, setExpandedTabs] = React.useState([]);
    const [isDataReady, setIsDataReady] = useState(false);
    const [addedItineraries, setAddedItineraries] = React.useState([]);
    const [date, setDate] = React.useState(dayjs(new Date()).add(3, 'day'));
    const [quote, setQuote] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [availableDate, setAvailableDate] = useState(true);
    const [availabilityHours, setAvailabilityHours] = useState(true);
    const [selectedHour, setSelectedHour] = useState(null);
    const [fetchParams, setFetchParams] = useState(null);
    const [recommendations, setRecommendations] = useState(null);
    const [minPax, setMinPax] = useState(0);
    const suggestedTours = useFetchSuggestTours(fetchParams);
    const checkoutTours = useSelector(selectCartItems);
    const [addedTickets, setAddedTickets] = React.useState([]);
    const [city, setCity] = useState(null);
    const [tags, setTags] = useState([]);


    // ─── Helpers de normalización (JS) ────────────────────────────────────────────
    const norm = v => String(v ?? '');

    const getActivityKey = (a) =>
        norm(a?.id ?? a?.activity_id ?? a?.attraction_id ?? a?.code ?? a?.slug ?? a?.name);

    const ensureActivityObject = (a) => {
        if (a && typeof a === 'object') {
            const id = getActivityKey(a);
            return { ...a, id, name: a?.name ?? id };
        }
        const id = norm(a);
        return { id, name: id, value: 1 };
    };

    const normalizeAttractions = (list = []) => (
        (Array.isArray(list) ? list : [])
            .map(ensureActivityObject)
            .filter(a => Boolean(getActivityKey(a)))
    );

    const { attractions, isLoadingAtt } = useFetchAttractions(city?.slug);

    /**
     * Unifica la lista de actividades del booking para que esté disponible
     * en todos los alias que Step2/Tour/Checkout podrían leer.
     * Preferencia: overrideList (si lo pasas) > options.selectedAttractions > extras.selectedAttractions > t.extras.activities > t.extras.attractions > customizations.attractions
     */
    const unifyExtras = (t = {}, overrideList = null) => {
        // const extrasObj = t.extras || {};
        const optionsObj = t.options || {};
        const custom = t.customizations || {};

        const base =
            overrideList ??
            optionsObj.selectedAttractions ??
            // extrasObj.selectedAttractions ??
            // extrasObj.activities ??
            // extrasObj.attractions ??
            custom.attractions ??
            [];

        const unified = (base || []).map(ensureActivityObject);

        return {
            ...t,
            options: {
                ...(t.options || {}),
                selectedAttractions: unified, // canonical location
            },
            customizations: {
                ...(t.customizations || {}),
                attractions: unified, // Step2 suele leer aquí
            },
        };
    };

    // Igualdad robusta: si hay id en ambos, compara id + type; si no, cae a name+type
    const sameTicket = (a, b) => {
        const aKey = getActivityKey(a);
        const bKey = getActivityKey(b);
        if (aKey && bKey && aKey === bKey) {
            return norm(a?.type) === norm(b?.type);
        }
        return (norm(a?.name) === norm(b?.name)) && (norm(a?.type) === norm(b?.type));
    };

    const listHasTicket = (list = [], key) => {
        if (!key || !key.id) return false;
        return list.some(item => getActivityKey(item) === key.id);
    };

    const normalizePaxText = (value) =>
        String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();

    const isAdultParticipant = (paxItem) => {
        const title = normalizePaxText(paxItem?.title);
        return title.includes('adult') || title.includes('mayor');
    };

    const getAdultPaxCount = (paxList = []) =>
        (Array.isArray(paxList) ? paxList : [])
            .filter(isAdultParticipant)
            .reduce((sum, item) => sum + (Number(item?.value) || 0), 0);

    const cartEntryForCurrentTour = useMemo(
        () => checkoutTours?.find(ct => ct.slug === params.slug),
        [checkoutTours, params.slug]
    );

    const cartAttractions = useMemo(() => {
        if (!cartEntryForCurrentTour) return [];
        return normalizeAttractions(
            cartEntryForCurrentTour.options?.selectedAttractions ??
            cartEntryForCurrentTour.customizations?.attractions ??
            []
        );
    }, [cartEntryForCurrentTour]);

    // Keep addedTickets in sync with cart entry for this tour.
    useEffect(() => {
        setAddedTickets(cartAttractions);
    }, [cartAttractions]);

    const [participants, setParticipants] = React.useState([])
    const handleScrollToTop = () => {
        window.scrollTo(0, 0); // Mueve la pantalla al inicio (x = 0, y = 0)
    };

    const targetRef = useRef(null);

    const dispatch = useDispatch();

    // Local editor state for opening modal/dialog on this page
    const [editorSelectedTour, setEditorSelectedTour] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const addedActivityIds = useMemo(
        () => new Set((addedTickets || []).map(a => getActivityKey(a)).filter(Boolean)),
        [addedTickets]
    );

    const handleCloseEditor = () => setEditorOpen(false);

    const onAddedToCart = (tourObj) => {
        // tourObj is the object returned by prePrareTourBookingData
        // If the tour is already in the cart, prefer that stored entry so the editor
        // loads exactly what is saved in the booking. Otherwise fall back to the
        // freshly prepared booking object.
        const cartEntry = checkoutTours?.find(ct => ct.slug === (tourObj?.slug || params.slug));
        if (cartEntry) {
            // ensure the editor object contains the full tourDetails so modal can
            // map extras (attractions, food, custom) to full objects when needed
            const merged = { ...cartEntry, tourDetails: cartEntry.tourDetails || tour?.tourDetails || tourDetails };
            setEditorSelectedTour(merged);
        } else {
            const merged = { ...tourObj, tourDetails: tourObj.tourDetails || tour?.tourDetails || tourDetails };
            setEditorSelectedTour(merged);
        }

        setEditorOpen(true);
    }

    // Merge selections coming from the modal into editorSelectedTour so the page UI reflects modal changes live
    const mergeEditorSelections = (selections = {}) => {
        const selectedHourFromSelections =
            selections?.schedule?.selectedHour ??
            selections?.schedule ??
            null;

        // Precompute normalized activities (full objects) from selections so we can
        // update state synchronously and run side-effects afterwards (avoids setState-in-render)
        let finalActivities = null;
        if (selections.attractions) {
            const selectedByKey = new Map(
                (selections.attractions || [])
                    .map((a) => {
                        const key = getActivityKey(a);
                        if (!key) return null;
                        const rawQty = Number(a?.quantity ?? a?.value);
                        const quantity = Number.isFinite(rawQty) && rawQty > 0 ? Math.floor(rawQty) : 1;
                        const rateQuantities = (a && typeof a === 'object' && a.rate_quantities && typeof a.rate_quantities === 'object')
                            ? { ...a.rate_quantities }
                            : undefined;
                        return [key, { ...a, id: key, name: a?.name ?? key, quantity, rate_quantities: rateQuantities }];
                    })
                    .filter(Boolean)
            );
            const keys = Array.from(selectedByKey.keys());
            const availableAttractions =
                editorSelectedTour?.tourDetails?.extras?.attractions ||
                tour?.tourDetails?.extras?.attractions ||
                [];
            const matched = availableAttractions.filter(a => keys.includes(getActivityKey(a)));
            finalActivities = (matched.length > 0 ? matched : keys.map(k => ({ id: k, name: k })))
                .map(a => {
                    const key = getActivityKey(a);
                    const selected = selectedByKey.get(key);
                    return {
                        ...a,
                        id: key,
                        name: a?.name ?? key,
                        quantity: Number(selected?.quantity) > 0 ? Number(selected.quantity) : 1,
                        rate_quantities: (selected && typeof selected === 'object' && selected.rate_quantities && typeof selected.rate_quantities === 'object')
                            ? { ...selected.rate_quantities }
                            : undefined,
                    };
                });
        }

        // Update editorSelectedTour (no side-effects inside the updater)
        setEditorSelectedTour(prev => {
            if (!prev) return prev;
            const custom = { ...(prev.customizations || {}) };
            const options = { ...(prev.options || {}) };
            if (selections.travelCompanion) custom.travelCompanion = selections.travelCompanion;
            if (selections.language) custom.language = selections.language;
            if (selections.guide) custom.guide = selections.guide;
            if (selections.vehicle) custom.vehicle = selections.vehicle;
            if (selections.meetingPoint) {
                custom.meetingPoint = selections.meetingPoint;
                options.selectedMeetingPoint = selections.meetingPoint;
            }
            if (finalActivities !== null) custom.attractions = finalActivities;
            if (selectedHourFromSelections) {
                custom.schedule = { selectedHour: selectedHourFromSelections };
                options.selectedHour = selectedHourFromSelections;
            }
            return { ...prev, options, customizations: custom };
        });

        // Now run side-effects (outside the render/setState updater)
        if (finalActivities !== null) {
            setAddedTickets(finalActivities);

            // If this tour is in the cart, also update the cart entry so store and page stay in sync
            const cartEntry = checkoutTours?.find(ct => ct.slug === (params.slug));
            if (cartEntry) {
                try {
                    const tourBookingUpd = prePrareTourBookingData(
                        editorSelectedTour?.tourDetails || tour,
                        date,
                        participants,
                        null,
                        {
                            selectedAttractions: finalActivities,
                            availabilityHours: (editorSelectedTour?.options?.availabilityHours || tour?.availabilityHours),
                            selectedHour: (selectedHourFromSelections || editorSelectedTour?.options?.selectedHour || selectedHour)
                        }
                    );
                    dispatch(setExtraActivities({ url: params.slug, tourData: tourBookingUpd, extras: { selectedAttractions: finalActivities } }));
                } catch (e) {
                    console.warn('Failed updating cart extras from modal selection', e);
                }
            }
        }
    }

    // Keep editor copy synced with cart attractions while modal is open.
    // Guard against no-op updates to avoid render loops.
    useEffect(() => {
        if (!editorOpen) return;
        setEditorSelectedTour(prev => {
            if (!prev) return prev;
            if (prev.slug !== params.slug) return prev;

            const prevAttractions = (prev.options?.selectedAttractions || prev.customizations?.attractions || [])
                .map(a => {
                    const rq = (a && typeof a === 'object' && a.rate_quantities && typeof a.rate_quantities === 'object')
                        ? Object.entries(a.rate_quantities).map(([k, v]) => `${k}=${Number(v) || 0}`).sort().join('|')
                        : '';
                    return `${getActivityKey(a)}:${Number(a?.quantity ?? a?.value) || 1}:${rq}`;
                })
                .filter(Boolean);
            const nextAttractions = (cartAttractions || [])
                .map(a => {
                    const rq = (a && typeof a === 'object' && a.rate_quantities && typeof a.rate_quantities === 'object')
                        ? Object.entries(a.rate_quantities).map(([k, v]) => `${k}=${Number(v) || 0}`).sort().join('|')
                        : '';
                    return `${getActivityKey(a)}:${Number(a?.quantity ?? a?.value) || 1}:${rq}`;
                })
                .filter(Boolean);

            const sameLength = prevAttractions.length === nextAttractions.length;
            const sameValues = sameLength && prevAttractions.every((id, idx) => id === nextAttractions[idx]);
            if (sameValues) return prev;

            return {
                ...prev,
                options: {
                    ...(prev.options || {}),
                    selectedAttractions: cartAttractions
                },
                customizations: {
                    ...(prev.customizations || {}),
                    attractions: cartAttractions
                }
            };
        });
    }, [cartAttractions, editorOpen, params.slug]);

    const AvailabilityButton = ({ styles = {} }) => (
        <TagButton
            text={t('seeAvailability')}
            href="#personalizar-btn"
            onClick={(e) => {
                e.preventDefault();
                console.log("looking for availability")
                targetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            sx={{
                fontWeight: 600,
                mt: "1rem",
                px: "1rem",
                py: ".4rem",
                textAlign: 'center',
                width: "100%",
                ...styles,
            }}
        />
    )

    const rotateDay = (day) => (day === 0 ? 1 : day === 6 ? 0 : day + 1);
    const findNextValidDate = () => {
        let date = dayjs().add(tourDetails.cutoff.value, 'hours'); // Comienza desde el cutoff
        const available = tourDetails.availabilityDays.days.split(',').map(Number); // [1, 2, 3]

        // Encuentra la primera fecha válida
        for (let i = 0; i < 365; i++) { // Limitar a un año para evitar bucles infinitos
            let dayOfWeek = rotateDay(date.day());
            if (tourDetails?.cutoff && available.includes(dayOfWeek) && !date.isBefore(dayjs().add(tourDetails.cutoff.value, 'hours'), 'day')) {
                return date; // Fecha válida encontrada
            }
            date = date.add(1, 'day'); // Avanza al siguiente día
        }
        return null; // Si no se encuentra una fecha válida en un año
    };

    useEffect(() => {
        if (tourDetails && !isLoading) {
            // Establecer el estado del tour
            setTour(tourDetails);

            // Crear breadcrumbs
            setBreadcrumbs([
                {
                    name: `${t('breadcumnb-tours-title', { 'city': tourDetails.city?.name || "..." })}`,
                    path: `/${t('breadcumnb-tours', { 'city': tourDetails.city?.slug })}`,
                },
                {
                    name: tourDetails.name || t('loading'),
                    path: "#",
                },
            ]);

            setFetchParams({ tour: tourDetails.slug, city: tourDetails.city?.slug, limit: 3 })
            setCity(tourDetails.city);

            if (tourDetails.tags) {
                const tagsArray = tourDetails.tags.split('#').filter(Boolean);
                setTags(tagsArray);
            }

            if (tourDetails?.cutoff) {
                // console.log("cutoff", tourDetails);
                const nextAvailableDate = findNextValidDate();
                // console.log(nextAvailableDate);
                setDate(nextAvailableDate);
            }

            // Procesar los participantes
            if (tourDetails.paxRates) {
                const paxRates = tourDetails.paxRates.map((item) => ({
                    title: item.pax,
                    subtitle: item.age_range,
                    price: item.price,
                    min_pax: item.min_pax || 0,
                    value: item.value || 0,
                }));
                setParticipants(paxRates);
            }

            // if (tourDetails.minPax) {
            //     setMinPax(tourDetails.minPax);
            // }
        }
    }, [tourDetails, isLoading]);


    // Manejar la lógica cuando los datos sugeridos están listos
    useEffect(() => {
        if (suggestedTours?.tourList) {
            const toursRecommended = suggestedTours.tourList.map((tour) => ({
                title: tour.name,
                shortDescription: tour.shortDescription,
                price: tour.price,
                currency: tour.price?.currency,
                imageUrl: tour.cover,
                rate: tour.scores?.score,
                reviews: tour.reviewsTotal,
                city: { slug: tour.city.slug },
                slug: tour.slug,
                freeCancelation: tour.freeCancelation,
                duration: tour.duration,
                languages: tour.languages
            }));
            setRecommendations(toursRecommended);
        }
    }, [suggestedTours]);

    useEffect(() => {
        if (tourDetails && !isLoading) {
            const timer = setTimeout(() => {
                setIsDataReady(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [tourDetails, isLoading]);


    const getTextLanguages = (languages) => {
        if (languages === undefined) return ''
        // return languages.reduce((accumulator, currentValue, index) => {
        //     if (index !== 0) {
        //         return accumulator + ", " + currentValue?.language
        //     }
        //     return accumulator + currentValue?.language
        // }, "");
    };

    const { isMobile } = useScreenDetector()

    const hashTags = topActivities.slice(0, 3)
    const parent = tourData.breadCrums[tourData.breadCrums.length - 1];
    const tourCards = dataPage.activities.slice(0, 3);
    const durationFilters = [
        "3-5 " + t('hours'),
        "7-9 " + t('hours'),
        "12-14 " + t('hours'),
        t('Indifferent'),
    ]

    const onExpandTab = (tab) => {
        if (expandedTabs.includes(tab)) {
            setExpandedTabs(current => current.filter(item => item !== tab))
        } else {
            setExpandedTabs(current => [...current, tab])
        }
    }

    const addExtraTicket = (item, type) => {
        const adultPaxCount = getAdultPaxCount(participants);
        const ticketKey = {
            id: getActivityKey(item),          // ← clave estable
            name: item?.name ?? getActivityKey(item),
            type,
            price: item?.rates,
            rates: item?.rates,
            value: adultPaxCount,
            quantity: adultPaxCount,
        };

        const baseActivities = cartAttractions?.length > 0 ? cartAttractions : (addedTickets ?? []);

        const filteredTickets = baseActivities.filter(t => !sameTicket(t, ticketKey));
        // Solo permite agregar una actividad por id (no repite id)
        const alreadyExists = filteredTickets.some(t => t.id === ticketKey.id);
        const updatedTickets = alreadyExists ? filteredTickets : [...filteredTickets, ticketKey];

        setAddedTickets(updatedTickets);

        setEditorSelectedTour(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                customizations: {
                    ...(prev.customizations || {}),
                    attractions: updatedTickets
                }
            };
        });

        const tourBookingUpd = prePrareTourBookingData(tour, date, participants, null, { selectedAttractions: updatedTickets, availabilityHours: tour?.availabilityHours, selectedHour: selectedHour });
        setTourBooking(tourBookingUpd);

        const isInCart = checkoutTours?.some(ct => ct.slug === tour?.slug);
        if (isInCart) {
            dispatch(setExtraActivities({
                url: tour?.slug,
                tourData: tourBookingUpd,
                extras: { selectedAttractions: updatedTickets }
            }));
        } else {
            dispatch(addTour(tourBookingUpd));
        }
    };


    const removeExtraTicket = (ticketKey, item) => {
        // arma una llave robusta por si ticketKey vino sin id
        const keyId = getActivityKey(item);

        const updatedTickets = (addedTickets || []).filter(t => getActivityKey(t) !== keyId);
        setAddedTickets(updatedTickets);

        setEditorSelectedTour(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                customizations: {
                    ...(prev.customizations || {}),
                    attractions: updatedTickets
                }
            };
        });

        const tourBookingUpd = prePrareTourBookingData(tour, date, participants, null, { selectedAttractions: updatedTickets, availabilityHours: tour?.availabilityHours, selectedHour: selectedHour });
        setTourBooking(tourBookingUpd);

        const isInCart = checkoutTours?.some(ct => ct.slug === tour?.slug);
        if (isInCart) {
            dispatch(removeExtraActivities({
                url: tour?.slug,
                tourData: tourBookingUpd,
                extras: { selectedAttractions: updatedTickets }
            }));
        }
    };


    const onClickItinerary = (name) => {
        if (addedItineraries.find((item) => item.title === name)) {
            setAddedItineraries(current => current.filter(item => item.title !== name))
        } else {
            const found = tourData.itinerary.find((item) => item.title === name)
            setAddedItineraries(current => [...current, found])
        }
    }

    const handleChangeCalendar = (newDate) => {
        setDate(newDate)
    }

    const onModifyParticipants = (instruction) => (title) => {
        setParticipants(current => {
            return current.map(item => {
                if (item.title === title) {
                    const minValue = item.min_pax ?? 0;
                    let newValue = instruction === "add"
                        ? item.value + 1
                        : item.value - 1;

                    // No puede ser menor al mínimo
                    if (newValue < minValue) newValue = minValue;

                    return {
                        ...item,
                        value: newValue,
                    };
                }
                return item;
            });
        });
    }

    useEffect(() => {
        const { REACT_APP_BASE_URL } = process.env
        const token = localStorage.getItem('token');

        const paxConfig = participants.map(item => ({ paxtype: item.title, pax_total: item.value }));
        // console.log(addedTickets);
        axios.post(
            `${REACT_APP_BASE_URL}/index.php?r=api/quote&tour=${tour?.slug}&lng=${i18n.language}`,
            {
                "tour_quote": [
                    {
                        "pax": paxConfig,
                        date_tour: dayjs(date).format('YYYY-MM-DD'), // "2024-12-26"
                        "extras": addedTickets,
                    }
                ]
            },
            {
                headers: {
                    'x-authorization-token': token,
                    'Content-Type': 'application/json'  // Especificamos que estamos enviando JSON
                }
            }
        ).then(({ data }) => {
            const { tour } = data;
            const { quote } = tour[0];
            if (!quote) setAvailableDate(false);
            if (!quote) return;

            //set cutoff time

            setAvailableDate(true);
            setQuote(data);
        });

    }, [date, participants, tour, i18n.language, tourBooking]);

    const adultPaxCount = useMemo(() => getAdultPaxCount(participants), [participants]);

    useEffect(() => {
        if (!Array.isArray(addedTickets) || addedTickets.length === 0) return;
        if (!tour || !tour.slug) return;

        const syncedTickets = addedTickets.map((ticket) => ({
            ...ticket,
            value: adultPaxCount,
            quantity: adultPaxCount,
        }));

        const changed = syncedTickets.some((ticket, idx) => {
            const prev = addedTickets[idx];
            return Number(ticket?.value) !== Number(prev?.value) || Number(ticket?.quantity) !== Number(prev?.quantity);
        });
        if (!changed) return;

        setAddedTickets(syncedTickets);

        setEditorSelectedTour(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                customizations: {
                    ...(prev.customizations || {}),
                    attractions: syncedTickets
                }
            };
        });

        const tourBookingUpd = prePrareTourBookingData(
            tour,
            date,
            participants,
            null,
            { selectedAttractions: syncedTickets, availabilityHours: tour?.availabilityHours, selectedHour: selectedHour }
        );
        setTourBooking(tourBookingUpd);

        const isInCart = checkoutTours?.some(ct => ct.slug === tour?.slug);
        if (isInCart) {
            dispatch(setExtraActivities({
                url: tour?.slug,
                tourData: tourBookingUpd,
                extras: { selectedAttractions: syncedTickets }
            }));
        }
    }, [adultPaxCount, tour, date, selectedHour, dispatch]);

    // Rebuild booking whenever attractions/participants/date/hour change so PaymentSummary stays in sync.
    useEffect(() => {
        if (!tour || !tour.slug) return;
        try {
            const tb = prePrareTourBookingData(
                tour,
                date,
                participants,
                null,
                {
                    selectedAttractions: addedTickets,
                    availabilityHours: tour?.availabilityHours,
                    selectedHour
                }
            );
            setTourBooking(tb);
        } catch (e) {
            console.warn('Failed to sync tourBooking with current attractions', e);
        }
    }, [tour, date, participants, addedTickets, selectedHour]);

    if (!isDataReady) { // Mostrar esqueletos mientras carga o no hay tour
        return (
            <MainLayout>
                <Box sx={{ backgroundColor: "#ffffff", height: "fit-content", width: "100%" }}>
                    <Container>
                        <Skeleton variant="rectangular" height={40} /> {/* Breadcrumbs */}
                        <Box sx={{ display: "flex", flexDirection: "row", gap: "1rem", mb: "2rem", mt: "1rem" }}>
                            <Box sx={{ width: "70%" }}>
                                <Skeleton variant="text" width={200} height={40} /> {/* Titulo */}
                                <Skeleton variant="text" width={300} height={20} sx={{ mt: "0.5rem" }} /> {/* Descripcion corta */}
                                <Skeleton variant="rectangular" width={100} height={20} sx={{ mt: "0.5rem" }} /> {/* Rating */}
                            </Box>
                            <Box sx={{ width: "30%", textAlign: "right", pl: "15%" }}>
                                <Skeleton variant="text" width={100} height={40} /> {/* Precio */}
                                <Skeleton variant="rectangular" width={150} height={40} sx={{ mt: "0.5rem" }} /> {/* Boton */}
                            </Box>
                        </Box>
                        <Skeleton variant="rectangular" width="100%" height={300} sx={{ mb: "3rem" }} /> {/* Galeria */}
                        <Skeleton variant="rectangular" width="100%" height={200} sx={{ mb: "2rem" }} /> {/* Detalles del tour */}
                        <Skeleton variant="rectangular" width="100%" height={200} sx={{ mb: "2rem" }} /> {/* Itinerario */}
                        <Skeleton variant="rectangular" width="100%" height={150} sx={{ mb: "2rem" }} /> {/* Calendario/Resumen */}
                        <Skeleton variant="rectangular" width="100%" height={250} sx={{ mb: "3rem" }} />{/* Otras sugerencias */}
                    </Container>
                </Box>
            </MainLayout>
        );
    }


    return (
        error ? <ErrorPage /> :
            <MainLayout>
                <Helmet>
                    <title>{t('page-tour', { 'tour': tour?.name })}</title>
                    <meta
                        name="description"
                        content={tour?.content?.short_description}
                        data-react-helmet="true"
                    />
                </Helmet>
                <Helmet>
                    <meta
                        name="robots"
                        content="index, follow"
                        data-react-helmet="true"
                    />
                </Helmet>

                <Box sx={{
                    // backgroundColor: "#ffffff",
                    height: "fit-content",
                    width: "100%",
                }}>
                    <div className='pattern-img pattern-top' />
                    <Container>
                        {(breadcrumbs.length > 0) && <Breadcrumbs breadcrumbs={breadcrumbs} />}
                        <Box sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            mb: { xs: "1rem", md: "2rem" },
                        }}>
                            <Box sx={{ width: "70%" }}>
                                <Typography
                                    component="h1"
                                    sx={{
                                        color: "#3E3A39",
                                        fontSize: { xs: 20, md: 32 },
                                        fontFamily: 'Poppins',
                                        fontWeight: 700,
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {tour?.name}
                                </Typography>
                                <Typography sx={{ display: { xs: "none", md: "block" }, mt: "1rem" }}>
                                    {tour?.content?.short_description}
                                </Typography>
                                <Box sx={{ display: { xs: "none", md: "block" }, mt: "1rem" }} >
                                    <ReviewRatings scores={tour?.scores} reviewsTotal={tour?.reviewsTotal} />
                                </Box>
                            </Box>
                            <Box sx={{
                                display: "flex",
                                flexDirection: "column",
                                width: "30%",
                                textAlign: "right",
                                pl: "5%",
                            }}>
                                <Typography
                                    color="secondary"
                                    component="p"
                                    sx={{
                                        fontSize: { xs: 20, md: 24 },
                                        fontFamily: 'Poppins',
                                        fontWeight: 700,
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {`${t('from')} ${tour?.rates?.min_price} ${tour?.rates?.currency}`}
                                </Typography>
                                <Typography
                                    color="grayText"
                                    sx={{
                                        fontSize: { xs: '10px', md: '12px' },
                                        fontWeight: 400
                                    }}>
                                    {tour?.tax > 0 ? t('IVANotincluded') : ''}
                                </Typography>

                                <AvailabilityButton styles={{ display: { xs: "none", md: "block" } }} />
                            </Box>
                        </Box>
                        <div className='pattern-img pattern-top' />
                        <Box sx={{ mb: { xs: "1rem", md: "3rem" } }}>
                            {tourDetails !== undefined && <GridGallery images={tour.images} tour={tour?.name} />}
                        </Box>
                        <Box sx={{ display: { xs: "block", md: "none" } }}>
                            <RichText
                                text={tour?.content?.short_description}
                            />
                        </Box>
                        <Box sx={{ display: { xs: "block", md: "none" }, mt: "1rem" }} >
                            <ReviewRatings scores={tour?.scores} reviewsTotal={tour?.reviewsTotal} />
                        </Box>
                        <AvailabilityButton styles={{ display: { xs: "block", md: "none" }, mb: "2rem" }} />
                        <Box sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            width: "100%"
                        }}>
                            <Box sx={{ width: { xs: "100%", md: "70%" } }}>
                                <DetailsSection
                                    title={t('about-tour')}
                                    isExpanded={
                                        !isMobile ? true : expandedTabs.includes(t('about-tour'))
                                    }
                                    onCollapse={onExpandTab}
                                >
                                    {tour?.content?.policyCancellation && <Box sx={{ my: "1rem" }}>
                                        <Box sx={{
                                            alignItems: "center",
                                            display: "flex",
                                            flexDirection: "row",
                                            mb: "1rem",
                                        }}>
                                            <ClockIcon width={24} height={24} />
                                            <Typography color="secondary" sx={{ ml: "4px" }}>
                                                {
                                                    tourData.freeCancelation ?
                                                        t('freecancelation') :
                                                        t('cancelation-nonrefundable')
                                                }
                                            </Typography>
                                        </Box>
                                        <RichText
                                            styles={{ wfontSize: 16, fontFamily: 'Inter' }}
                                            text={tour?.content?.policyCancellation}
                                        />
                                    </Box>}
                                    <Box sx={{ mb: "2rem" }}>
                                        <Box sx={{
                                            alignItems: "center",
                                            display: "flex",
                                            flexDirection: "row",
                                            mb: "1rem",
                                        }}>
                                            <GlobeIcon width={24} height={24} />
                                            <Typography color="secondary" sx={{ ml: "4px" }}>
                                                {tour?.languages.map((item, index) => {
                                                    return index == 0 ? `${t('language')}: ` + item.language : (index == 1 ? ` | ${t('alsoavailable')}: ` + item.language : `, ` + item.language);
                                                })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </DetailsSection>
                                <DetailsSection
                                    title={t('tourdetails')}
                                    isExpanded={
                                        !isMobile ? true : expandedTabs.includes(t('tourdetails'))
                                    }
                                    onCollapse={onExpandTab}
                                >
                                    <Box sx={{
                                        borderBottom: "1px solid #666B6E33",
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        mt: "1rem",
                                        width: "100%",
                                        pb: "1rem",
                                    }}>
                                        <Typography component="h3" color="secondary" sx={{
                                            fontSize: 16,
                                            width: { xs: "100%", md: "30%" },
                                            marginTop: "1rem"
                                        }}>
                                            {t('tourdetails-description')}
                                        </Typography>
                                        <Box sx={{ width: { xs: "100%", md: "70%" } }}>
                                            <RichText
                                                styles={{ wfontSize: 16, fontFamily: 'Inter' }}
                                                text={tour?.content?.description}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        borderBottom: "1px solid #666B6E33",
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        // mt: "1rem",
                                        width: "100%",
                                        pb: "1rem",
                                    }}>
                                        <Typography component="h3" color="secondary" sx={{
                                            fontSize: 16,
                                            width: { xs: "100%", md: "30%" },
                                            marginTop: "1rem"
                                        }}>
                                            {t('tourdetails-included')}
                                        </Typography>
                                        <RichText
                                            styles={{ fontSize: 16, fontFamily: 'Inter' }}
                                            text={tour?.content?.include}
                                        />
                                    </Box>
                                    <Box sx={{
                                        borderBottom: "1px solid #666B6E33",
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        // my: "2rem",
                                        width: "100%",
                                        pb: "1rem",
                                    }}>
                                        <Typography component="h3" color="secondary" sx={{
                                            fontSize: 16,
                                            width: { xs: "100%", md: "30%" },
                                            marginTop: "1rem"
                                        }}>
                                            {t('tourdetails-notincluded')}
                                        </Typography>
                                        <RichText
                                            styles={{ fontSize: 16, fontFamily: 'Inter' }}
                                            text={tour?.content?.notIncluded}
                                        />
                                    </Box>
                                </DetailsSection>
                                {(tour && tour?.extras?.attractions?.length > 0) &&
                                    <DetailsSection
                                        title={t('suggesteditinerary')}
                                        isExpanded={
                                            !isMobile ? true : expandedTabs.includes(t('suggesteditinerary'))
                                        }
                                        onCollapse={onExpandTab}
                                    >
                                        {/* Atracciones y actividades extras */}
                                        {tour && tour?.extras?.attractions.map((item, index) => {
                                            return (
                                                <div key={index} style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "1.5rem", marginTop: "1.2rem" }}>
                                                    <Typography key={`h2_${index}`} component="h3" sx={{
                                                        color: "#223F7F",
                                                        fontFamily: "Poppins",
                                                        fontSize: "18px",
                                                        fontWeight: 900,
                                                        marginTop: "5px",
                                                        marginBottom: "5px",
                                                        display: "flex",
                                                        gap: "8px",
                                                        alignItems: "flex-end",
                                                        lineHeight: 1,
                                                    }}>
                                                        {item.icon &&
                                                            <div
                                                                dangerouslySetInnerHTML={
                                                                    {
                                                                        __html:
                                                                            (item.icon) ? item.icon.replace(/height="\d+px"/, 'height="32px"')
                                                                                .replace(/width="\d+px"/, 'width="32px"') : ''
                                                                    }
                                                                }
                                                                style={{ width: "32px", height: "32px", display: "flex", alignItems: "flex-end" }}
                                                            />
                                                        }

                                                        {item.name}
                                                    </Typography>
                                                    <Typography key={`p_${index}`} component="p" sx={{
                                                        color: "#223F7F",
                                                        fontFamily: "Poppins",
                                                        fontSize: "16px",
                                                        fontWeight: 400,
                                                        marginBottom: "5px",
                                                    }}>
                                                        {item.description}
                                                    </Typography>

                                                    {
                                                        item.duration &&
                                                        <Typography
                                                            key={`p_clock_${index}`}
                                                            component="p"
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "flex-end",
                                                                gap: "8px",
                                                                fontSize: "16px",
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            <ClockIcon width={21} height={21} />
                                                            {item.duration}
                                                        </Typography>
                                                    }


                                                    <div key={`div_${index}`} style={{ display: "flex", flexDirection: "row", gap: "1rem", flexWrap: "wrap" }}>
                                                        {item?.ticket_types.map((type, index) => {
                                                            // Build the ticket key
                                                            const ticketKey = {
                                                                id: getActivityKey(item),
                                                                name: item.name,
                                                                type: type
                                                            };

                                                            const isAdded = addedActivityIds.has(getActivityKey(item));

                                                            return <TagButton
                                                                endIcon={isAdded ? <CheckIcon /> : <PlusIcon />}
                                                                hoverIcon={isAdded ? <TrashIcon /> : <PlusIcon />}
                                                                key={index} text={`${t('addTicket')} ${type}`} onClick={() => isAdded ? removeExtraTicket(ticketKey, item) : addExtraTicket(item, type)} sx={{
                                                                    padding: '5px 15px',
                                                                    backgroundColor: isAdded ? "#223F7F" : "#40C5E8",
                                                                }} />
                                                        })}
                                                    </div>
                                                </div>
                                            )
                                        })}

                                    </DetailsSection>
                                }

                                <DetailsSection
                                    title={t('additionalinfo')}
                                    isExpanded={
                                        !isMobile ? true : expandedTabs.includes(t('additionalinfo'))
                                    }
                                    onCollapse={onExpandTab}
                                    infoIcon={true}
                                    infoDetails={tour?.operator}
                                >
                                    <Box
                                        sx={{
                                            borderBottom: "1px solid #666B6E",
                                            pb: "1rem",
                                            mt: "1rem",
                                        }}
                                    >

                                        <Typography component="h3" color="secondary" sx={{
                                            fontSize: 16,
                                        }}>
                                            {t('tourdetails-requirements')}
                                        </Typography>
                                        <RichText
                                            styles={{ fontSize: 16, fontFamily: 'Inter' }}
                                            text={tour?.content?.requirement}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            borderBottom: "1px solid #666B6E",
                                            pb: "1rem",
                                            mt: "1rem",
                                        }}
                                    >

                                        <Typography component="h3" color="secondary" sx={{
                                            fontSize: 16,
                                        }}>
                                            {t('tourdetails-instructions')}
                                        </Typography>
                                        <RichText
                                            styles={{ fontSize: 16, fontFamily: 'Inter' }}
                                            text={tour?.content?.instructions}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            borderBottom: "1px solid #666B6E",
                                            pb: "1rem",
                                            mt: "1rem",
                                        }}
                                    >

                                        <Typography component="h3" color="secondary" sx={{
                                            fontSize: 16,
                                        }}>
                                            {t('tourdetails-beforeyougo')}
                                        </Typography>
                                        <RichText
                                            styles={{ fontSize: 16, fontFamily: 'Inter' }}
                                            text={tour?.content?.additionalInformation}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            borderBottom: "1px solid #666B6E",
                                            pb: "1rem",
                                            mt: "1rem",
                                        }}
                                    >

                                        <Typography component="h3" color="secondary" sx={{
                                            fontSize: 16,
                                        }}>
                                            {tour?.isAccessible == 1 ? t('tourisAccessible') : t('tourisNotAccessible')}
                                        </Typography>
                                        <Typography>
                                            {tour?.isAccessible == 1 ?
                                                t('tourisAccessibleText') :
                                                t('tourisNotAccessibleText')
                                            }
                                        </Typography>
                                    </Box>
                                </DetailsSection>

                            </Box>

                            <Box ref={targetRef} sx={{ width: { xs: "100%", md: "30%" } }}>
                                <Box sx={{ mt: { xs: "1rem", md: "2rem" }, pl: { xs: 0, md: "2rem" } }}>
                                    {tour?.availabilityDays &&
                                        <CalendarTour cutOff={tour?.cutoff?.value} availableDays={tour?.availabilityDays.days} title={t('selectdate')} date={date} handleChange={handleChangeCalendar} />
                                    }
                                </Box>
                                <Box sx={{ mt: { xs: "1rem", md: "2rem" }, pl: { xs: 0, md: "2rem" } }}>
                                    {tour?.availabilityHours &&
                                        <Schedule title={t('selectschedule')} availabilityHours={[tour?.availabilityHours]} selectedHour={selectedHour} onChangeSelectedHour={(h) => {
                                            setSelectedHour(h);
                                            try {
                                                const tb = prePrareTourBookingData(tour, date, participants, null, { selectedAttractions: addedTickets, availabilityHours: tour?.availabilityHours, selectedHour: h });
                                                setTourBooking(tb);
                                            } catch (e) {
                                                console.warn('Failed to update tourBooking with selected hour', e);
                                            }
                                        }} ></Schedule>
                                    }
                                </Box>
                                <Box sx={{ mt: { xs: "1rem", md: "2rem" }, pl: { xs: 0, md: "2rem" } }}>
                                    <AddItemsButtonsFilter
                                        title={t('participants')}
                                        options={participants}
                                        onAdd={onModifyParticipants("add")}
                                        onRemove={onModifyParticipants("remove")}
                                        isExpanded={true}
                                    />
                                </Box>
                                <Box sx={{ mt: { xs: "1rem", md: "2rem" }, pl: { xs: 0, md: "2rem" } }}>
                                    <RadioButtonsFilter title={t('components:duration')} options={durationFilters} onChange={() => { }} />
                                </Box>
                                <Box sx={{ mt: { xs: "1rem", md: "2rem" }, pl: { xs: 0, md: "2rem" } }}>
                                    {availableDate ? <>
                                        {participants && <PaymentSummary items={participants} tourBooking={tourBooking} iva={21} tour={tour} extras={{
                                            selectedAttractions: addedTickets
                                        }} selectedHour={selectedHour} date={date} onAddedToCart={onAddedToCart} />}
                                    </> : <>
                                        <Alert severity="error">{t('noAvailability')}</Alert>
                                    </>}
                                </Box>

                            </Box>
                        </Box>
                        {/* <AdditionalInformationSection sections={tour?.content}></AdditionalInformationSection> */}
                        <FAQs faqs={tour?.faqs} />
                        <Typography component="h2" color="secondary" sx={{
                            fontSize: { xs: 24, md: 32 },
                            fontFamily: 'Poppins',
                            fontWeight: 'bolder',
                            lineHeight: 1.5,
                            mt: { xs: "3rem", md: "4rem" },
                            mb: { xs: "1rem", md: "2rem" },
                        }}>
                            {t('other-suggestions')}
                        </Typography>
                        <Box sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1", md: "repeat(3, 1fr)" },
                            gap: "1rem",
                            width: "100%",
                        }}>

                            {recommendations && recommendations.map((tour, index) => (
                                <Link style={{
                                    textDecoration: 'none',
                                    display: "flex",
                                    justifyContent: "center",

                                }} key={index} onClick={handleScrollToTop} to={`/tour/${tour.city.slug}/${tour.slug}`}>
                                    <MediaCard
                                        key={`${tour.title}${index}`}
                                        {...tour}
                                    />
                                </Link>
                            ))}
                        </Box>
                        {/* Render editor modal/dialog locally when requested from this page */}
                        {editorSelectedTour && <UpdateTourModal open={editorOpen} handleClose={handleCloseEditor} tour={editorSelectedTour} isMobile={isMobile} onSelectionsChange={mergeEditorSelections} />}
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: "2rem"
                        }}>
                            <TagButton
                                text={
                                    isMobile ?
                                        t('seemore') :
                                        t('other-suggestions-button', { "city": tour?.city?.name })
                                }
                                href={`/tours/${tour?.city?.slug}`}
                                color="white"
                                sx={{
                                    borderRadius: 8,
                                    border: "1px solid #40C5E8",
                                    color: "#40C5E8",
                                    px: "1rem",
                                    py: ".4rem",
                                    marginBottom: "1rem",
                                }}
                            />
                        </Box>
                        <Box sx={{ mt: '30px' }}>
                            <Typography color='secondary' variant='subtitle' component="h2" align="center">
                                {t('tours:attractions-list', { city: city?.name })}
                            </Typography>
                            <Box
                                sx={{
                                    display: {
                                        xs: 'flex',
                                        md: 'flex',
                                    },
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    gap: '1rem',
                                    py: '1rem',
                                    px: { xs: '.5rem', md: '1.5rem' },
                                    marginBottom: "1rem",
                                }}
                            >
                                {attractions.map((attraction, index) => {
                                    return (
                                        <TagButton
                                            key={`${attraction.name}${index}`}
                                            text={attraction.name}
                                            href={getHrefFromAttraction(attraction, t('guides:path'), t('guides:pathattractions', { city: attraction.city_slug }))}
                                            sx={{ height: '2rem', padding: '1.2rem 1rem' }}
                                            title={`${t("guideTabs-search-tagbutton")} ${attraction.name}`}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                        <Box sx={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
                            {tags && tags.length > 0 && (
                                tags.map((tag, index) => (
                                    <TagHashButton
                                        key={`${tag}${index}`}
                                        text={`#${tag}`}
                                        // href={`/tours/${tour?.city?.slug}?tag=${encodeURIComponent(tag)}`}
                                        sx={{ height: '2rem', padding: '1rem 1rem', marginRight: '1rem', marginBottom: '0.5rem' }}
                                        title={`${t("guideTabs-search-tagbutton")} ${tag}`}
                                    />
                                ))
                            )}
                        </Box>
                    </Container>
                </Box>
            </MainLayout>
    )
}

export default Tour
